import React, { useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HeroStars = () => {
  const stars = useMemo(
    () =>
      Array.from({ length: 42 }).map((_, i) => ({
        id: i,
        top: Math.random() * 44,
        left: Math.random() * 100,
        size: Math.random() * 2 + 1,
        delay: Math.random() * 4,
        dur: Math.random() * 3 + 3,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 z-[2]" aria-hidden="true">
      {stars.map((s) => (
        <span
          key={s.id}
          className="star"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.dur}s`,
            boxShadow: "0 0 5px 1px rgba(243,228,176,0.85)",
          }}
        />
      ))}
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const scrollHintOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative flex min-h-[100svh] w-full flex-col items-center justify-center overflow-hidden px-5 pt-28 pb-24 sm:px-8"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img
          src="/brand/header-bg.webp"
          alt="The Club at Renaissance under a starlit sky"
          className="h-full w-full object-cover"
          style={{ objectPosition: "center 32%" }}
        />
      </motion.div>

      <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-[#0d1530]/70 via-[#141c33]/25 to-[#17253F]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-40 bg-gradient-to-t from-[#17253F] to-transparent" />

      <HeroStars />

      <div className="relative z-10 flex w-full flex-col items-center text-center sm:-mt-64">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[135%] w-[130%] -translate-x-1/2 -translate-y-1/2"
          style={{ background: "radial-gradient(ellipse at center, rgba(9,17,34,0.6) 0%, rgba(9,17,34,0.32) 42%, rgba(9,17,34,0) 68%)" }}
        />
        <h1 className="contents">
          <img
            src="/brand/logo-cream.png"
            alt="Million Dollar Shot Fort Myers"
            className="w-[230px] object-contain drop-shadow-lg sm:w-[290px]"
          />
          <span className="sr-only">Million Dollar Shot Fort Myers</span>
        </h1>

        <p className="mt-3 font-display text-lg italic text-mds-cream/85">
          Benefiting <span className="font-bold not-italic">Naples Cancer Advisors</span>
        </p>

        <div className="mt-8 [text-shadow:0_2px_18px_rgba(0,0,0,0.45)]">
          <span className="block font-display smallcaps text-3xl font-medium leading-tight tracking-tight text-mds-cream sm:text-5xl">
            Saturday, November 14, 2026
          </span>
          <span className="mt-2 block font-display smallcaps text-lg font-bold tracking-wide text-mds-goldlt [text-shadow:0_2px_12px_rgba(0,0,0,0.85)] sm:text-2xl">
            5:30 to 9:30 pm &nbsp;·&nbsp; The Club at Renaissance &nbsp;·&nbsp; Fort Myers
          </span>
        </div>

        <div className="mt-9 flex flex-col items-center gap-4 sm:flex-row">
          <a
            href="https://secure.qgiv.com/event/mdsnaples2026"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-mds-gold px-9 py-3.5 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-mds-cream transition-colors duration-300 hover:bg-mds-goldlt hover:text-mds-navy"
            data-testid="hero-buy-tickets"
          >
            Reserve Your Table
          </a>
          <a
            href="#become-sponsor"
            className="rounded-full border border-mds-cream/50 px-9 py-3.5 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-mds-cream backdrop-blur-sm transition-colors duration-300 hover:bg-mds-cream hover:text-mds-navy"
            data-testid="hero-donate"
          >
            Explore Sponsorship
          </a>
        </div>

        <p className="gold-glimmer mt-6 font-sans text-[11px] font-extrabold uppercase tracking-[0.16em]" data-testid="hero-soldout-note">
          Limited Tables and Individual Seats Remaining — VIP Tables and VIP Seats SOLD OUT
        </p>
      </div>

      <div className="relative z-10 mt-14 flex flex-col items-center gap-5 sm:absolute sm:bottom-9 sm:left-1/2 sm:mt-0 sm:-translate-x-1/2">
        <a
          href="https://naplescanceradvisors.org/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Naples Cancer Advisors"
          className="transition-transform duration-300 hover:scale-[1.03]"
        >
          <img
            src="/brand/nca-logo.png"
            alt="Naples Cancer Advisors"
            className="h-24 w-auto object-contain opacity-95 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)] sm:h-40"
          />
        </a>
        <motion.div
          aria-hidden="true"
          data-testid="hero-scroll-hint"
          style={{ opacity: scrollHintOpacity }}
          className="flex flex-col items-center gap-4"
        >
          <motion.div
            data-testid="hero-scroll-mouse"
            animate={{ y: [0, 7, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-1 text-mds-cream/70"
          >
            <svg width="26" height="40" viewBox="0 0 26 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1.25" y="1.25" width="23.5" height="37.5" rx="11.75" stroke="currentColor" strokeWidth="2.2" />
              <rect x="11.4" y="8.5" width="3.2" height="9" rx="1.6" fill="currentColor" />
            </svg>
            <svg width="22" height="12" viewBox="0 0 22 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 2L11 10L20 2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.div>
          <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-mds-cream/70">
            Scroll to Explore
          </span>
        </motion.div>
      </div>
    </section>
  );
};
